import React, { useEffect, useState, useRef } from "react";
import styles from "./notificationPad.module.css";
import { getProfileStatusChanges } from "services/profileManegement/get-profile-status-changes";
import { IoNotificationsOutline } from "react-icons/io5";
import { profileStatusLabels } from "utils/bookingPopup";

interface StatusChange {
    full_name: string;
    old_status: string | null;
    new_status: string | null;
    changed_at: string;
}


export default function NotificationPad() {
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState<StatusChange[]>([]);
    const [loading, setLoading] = useState(true);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const PAGE_SIZE = 10;
    const listRef = useRef<HTMLUListElement>(null);

    const formatDateTime = (isoDate: string): string => {
        const date = new Date(isoDate);

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${day}.${month}.${year}, ${hours}:${minutes}`;
    };

    const isRecent = (dateStr: string) => {
        const changedDate = new Date(dateStr);
        const now = new Date();
        const diffInMs = now.getTime() - changedDate.getTime();
        const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
        return diffInDays <= 3;
    };

    const recentNotifications = notifications.filter((n, i, self) =>
        isRecent(n.changed_at)
    );

const unreadCount = recentNotifications.length;


    const fetchData = async (reset = false) => {
    try {
        setLoading(true);

        const currentScroll = listRef.current?.scrollTop ?? 0;

        const result = await getProfileStatusChanges(reset ? 0 : offset, PAGE_SIZE);
        if (result.success) {
            const newNotifications = result.data;
            if (reset) {
                setNotifications(newNotifications);
                setOffset(PAGE_SIZE);
            } else {
                setNotifications((prev) => [...prev, ...newNotifications]);
                setOffset((prev) => prev + PAGE_SIZE);

                setTimeout(() => {
                    if (listRef.current) {
                        listRef.current.scrollTop = currentScroll;
                    }
                }, 0);
            }
            setHasMore(newNotifications.length === PAGE_SIZE);
        } else {
            setHasMore(false);
        }
    } catch (error) {
        console.error("Fehler beim Laden der Statusänderungen:", error);
    } finally {
        setLoading(false);
    }
};

    const handleOpen = () => {
        setOpen((prev) => {
            const newState = !prev;
            if (newState && notifications.length === 0) {
                fetchData(true);
            }
            return newState;
        });
    };
    useEffect(() => {
        fetchData(true);
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div className={styles.container} ref={dropdownRef}>
            <button className={styles.bellButton} onClick={handleOpen}>
                <IoNotificationsOutline size={24} />
                {unreadCount > 0 && (
                    <span className={styles.badge}>{unreadCount}</span>
                )}
            </button>

            {open && (
                <div className={styles.dropdown}>
                    <h4 className={styles.header}>Benachrichtigungen</h4>
                    {loading ? (
                        <p className={styles.loading}>Lädt...</p>
                    ) : notifications.length === 0 ? (
                        <p className={styles.empty}>Keine neuen Benachrichtigungen</p>
                    ) : (
                        <ul className={styles.list} ref={listRef}>
                            {notifications.map((item, index) => (
                                <li key={index} className={styles.item}>
                                    <div className={styles.statusLine}>
                                        <strong className={styles.name}>{item.full_name}</strong>&nbsp;
                                        <span>hat seinen Status geändert:</span>&nbsp;
                                        <span className={styles.old}>
                                            {profileStatusLabels[item.old_status ?? "other"] || "–"}
                                        </span>
                                        <span className={styles.arrow}>→</span>
                                        <span className={styles.new}>
                                            {profileStatusLabels[item.new_status ?? "other"] || "–"}
                                        </span>
                                    </div>
                                    <div className={styles.date}>
                                        {formatDateTime(item.changed_at)}
                                    </div>
                                </li>
                            ))}

                            {hasMore && !loading && (
                                <button
                                    className={styles.loadMore}
                                    onClick={() => fetchData()}
                                >
                                    Mehr anzeigen
                                </button>
                            )}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

